'use client';

import { CSSProperties, ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { useInternalPathname } from 'hooks/navigation/useInternalPathname';
import { PerformanceLabLenisRoot } from './PerformanceLabLenisRoot';
import { PerformanceLabIndiaCartShell } from './PerformanceLabIndiaCartShell';
import { PerformanceLabNavbar } from './PerformanceLabNavbar';
import { FloatingBookVisitCTA } from './FloatingBookVisitCTA';
import { PerformanceLabMapsPreconnect } from './PerformanceLabMapsPreconnect';
import { bebasNeue, jetbrainsMono } from 'utils/fonts';
import { MasterTimelineProvider } from 'contexts/MasterTimelineContext';
import styles from './PerformanceLabChrome.module.css';

type ChromeVariant = {
  background: string;
  withCartShell: boolean;
  /** Mount the India cart provider for every region (IV Lounge lineup). */
  alwaysProvideCart?: boolean;
  ariaKey: string;
  ariaDefault: string;
};

// Per-route chrome differences, keyed off the (region-stripped) pathname.
// Default (root /performance-lab) is grey with the India cart shell.
function getChromeVariant(pathname: string): ChromeVariant {
  if (pathname.includes('/performance-lab/contact')) {
    return {
      background: '#fff',
      withCartShell: false,
      ariaKey: 'contactPage.mainAria',
      ariaDefault: 'Performance Lab Contact',
    };
  }
  if (pathname.includes('/performance-lab/recovery')) {
    return {
      background: '#ededed',
      withCartShell: true,
      ariaKey: 'recoveryPage.mainAria',
      ariaDefault: 'Performance Lab Recovery',
    };
  }
  if (pathname.includes('/performance-lab/iv-lounge')) {
    return {
      background: '#ededed',
      withCartShell: true,
      alwaysProvideCart: true,
      ariaKey: 'ivLounge.mainAria',
      ariaDefault: 'Performance Lab IV Lounge',
    };
  }
  if (pathname.includes('/performance-lab/glp')) {
    return {
      background: '#ededed',
      withCartShell: true,
      ariaKey: 'glp.mainAria',
      ariaDefault: 'Performance Lab GLP-1 Led Longevity Program',
    };
  }
  return {
    background: '#ededed',
    withCartShell: true,
    ariaKey: 'page.mainAria',
    ariaDefault: 'Performance Lab',
  };
}

// Shared chrome for the Performance Lab pages that share the navbar + Lenis
// scroll stack (root, recovery, contact). Rendered once from the (chrome)
// route-group layout so each page only renders its own sections.
export const PerformanceLabChrome = ({ children }: { children: ReactNode }) => {
  const t = useTranslations('performance-lab');
  const pathname = useInternalPathname();
  const variant = getChromeVariant(pathname);

  const main = (
    <PerformanceLabLenisRoot>
      <main
        role="main"
        aria-label={t.has(variant.ariaKey) ? t(variant.ariaKey) : variant.ariaDefault}
        data-cart-offset={variant.withCartShell ? 'true' : undefined}
        style={{ '--pl-main-bg': variant.background } as CSSProperties}
        className={styles.main}
      >
        <PerformanceLabNavbar />
        {children}
        <FloatingBookVisitCTA />
      </main>
    </PerformanceLabLenisRoot>
  );

  return (
    <MasterTimelineProvider>
      <PerformanceLabMapsPreconnect />
      <div
        className={`${jetbrainsMono.variable} ${bebasNeue.variable}`}
      >
        {variant.withCartShell ? (
          <PerformanceLabIndiaCartShell
            alwaysProvide={variant.alwaysProvideCart}
          >
            {main}
          </PerformanceLabIndiaCartShell>
        ) : (
          main
        )}
      </div>
    </MasterTimelineProvider>
  );
};

export default PerformanceLabChrome;
